import { useRef } from 'react';

import { MessagesSquare, Wrench } from 'lucide-react';

import { cn } from '@/shadcn/lib/utils';

import { count } from '@/components/traceInspector/traceInspector.utils';
import { AvatarComponent } from '@/components/ui/avatar/avatar';
import { DynamicIcon } from '@/components/ui/dynamicIcon/dynamicIcon';
import Tooltip from '@/components/ui/utils/tooltip';
import { shortFormat } from '@/utils/dates';
import { useToggle } from '@/utils/hooks';
import type { LinkQueryResult } from '../traceInspector.types';

type Props = {
  link: LinkQueryResult;
  isActive: boolean;
  interval: string | null;
  isFirst: boolean;
  isLast: boolean;
  answersCount: number | undefined;
  /** A revealed technical migration link: marked so it cannot read as a business event. */
  isMigration?: boolean;
};

export const Segment = ({
  link,
  isActive,
  interval,
  isFirst,
  isLast,
  answersCount = 0,
  isMigration = false
}: Props) => {
  const listViewRef = useRef<HTMLDivElement>(null);
  const [showTooltip, toggleShowTooltip] = useToggle(false);

  const getActionName = (): string => {
    const { action, actionKey, referencedBy, references } = link;
    const what = action ? action.title : actionKey;
    const postfix =
      !!referencedBy.totalCount || !!references.totalCount ? '*' : '';
    return `${postfix}${what.slice(0, 35).trim()}${what.slice(35) && '…'}`;
  };

  const actionName = getActionName();

  return (
    <div
      className={cn(
        'group relative z-1 mb-4 flex w-[161px] appearance-none flex-col items-end justify-start border-none bg-transparent p-0 focus:outline-hidden',
        'before:absolute before:top-20 before:left-0 before:z-[-1] before:h-px before:w-20 before:bg-border',
        'after:absolute after:top-20 after:right-0 after:z-[-1] after:h-px after:w-20 after:bg-border',
        { 'w-[126px]': isFirst && isLast },
        { 'w-[126px] before:w-0': isFirst && !isLast },
        isMigration && 'text-muted-foreground italic'
      )}
      data-is-migration={isMigration || undefined}
      title={isMigration ? `${actionName} (technical migration)` : actionName}
    >
      <div
        className={cn(
          'w-[126px] opacity-70 transition-opacity duration-200 group-hover:opacity-100',
          isActive && 'opacity-100'
        )}
      >
        <div className="mx-auto h-[14px] w-[10px] overflow-hidden">
          <svg
            aria-hidden="true"
            className={cn(
              '-translate-y-full block h-[10px] w-[10px] fill-primary-deep transition-transform duration-200',
              { 'translate-y-0': isActive }
            )}
            viewBox="0 0 100 100"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M50 89.489L0 0h100L50 89.489z" />
          </svg>
        </div>
        <div className="relative top-[3px] py-[3px] text-center font-normal text-[9px] text-card-foreground/80 leading-4">
          {count(link.height)}
        </div>
        <div className="truncate text-center font-bold text-[11px] leading-4">
          {actionName}
        </div>
        {!!answersCount && (
          <>
            <div
              className="absolute top-0 right-0 bg-card px-2 py-1 text-xs shadow-xs"
              onMouseEnter={toggleShowTooltip}
              onMouseLeave={toggleShowTooltip}
              ref={listViewRef}
            >
              <MessagesSquare className="text-primary" size={20} />
            </div>
            {showTooltip && (
              <Tooltip
                arrowUp={<div className="border-card border-b-[6px]" />}
                clientEl={listViewRef.current}
                portalEl={document.body}
              >
                <div className="flex justify-center gap-1 pt-2">
                  {answersCount} answer{answersCount > 1 && 's'}
                </div>
              </Tooltip>
            )}
          </>
        )}
      </div>
      <div
        className="relative mb-[2px] flex"
        data-is-first={isFirst}
        data-is-last={isLast}
      >
        <div className="relative top-[2px] z-1 flex w-[35px] shrink-0 items-center justify-center">
          {interval && (
            <div className="max-w-[35px] shrink-0 truncate bg-card px-[2px] pb-[6px] text-[9px] text-muted-foreground">
              {interval}
            </div>
          )}
        </div>
        <div className="mx-auto grid h-[60px] grid-flow-col grid-cols-[42px_38px_42px] grid-rows-1 items-center gap-1.5">
          <div
            className={cn(
              'relative col-start-2 flex h-9 w-11 items-center justify-center bg-card px-1 text-card-foreground opacity-70 group-hover:opacity-100',
              (isActive || isLast) && 'text-card-foreground opacity-100'
            )}
          >
            {isMigration ? (
              <Wrench
                aria-label="Technical migration"
                data-testid="segment-migration-icon"
                size={28}
              />
            ) : (
              <DynamicIcon icon={link.action?.icon || 'Doc'} size={35} />
            )}
          </div>
        </div>
      </div>
      <div
        className={cn(
          'w-[126px] opacity-70 transition-opacity duration-200 group-hover:opacity-100',
          isActive && 'opacity-100'
        )}
        data-is-active={isActive}
      >
        <div className="text-center text-[10px] text-card-foreground/80">
          {shortFormat(link.createdAt)}
        </div>
        <div className="w-full truncate text-center font-bold text-[11px]">
          {link.group.name}
        </div>
        <div
          className="relative z-1 mx-auto mt-1 h-8 w-9 shrink-0 bg-card"
          data-is-active={isActive}
        >
          <AvatarComponent
            name={link.group.name}
            size={36}
            source={link.group.avatar}
          />
        </div>
      </div>
    </div>
  );
};
